import { Button, Collapse, Spin } from 'antd';
import { Constants } from 'utils/constants';
import {
  mapStateDataToApi,
  mapStateToHasFilters,
} from 'pages/funding/Funding.mapper';
import styles from './FundingFilters.module.scss';
import { ProgramFilterStatusBackEndKey } from 'utils/enums';

/**
 * @component
 *
 * @summary
 * Renders the search input and collapsible filter panel for the Funding page.
 *
 * @description
 * This component displays a keyword search input and a collapsible section
 * containing various filters. It receives pre-configured filter items and
 * a clear action from the parent or hook.
 *
 * @param {Object} props - Component props.
 * @param {Array<Object>} props.items - Pre-configured collapse items for Ant Design Collapse.
 * @param {Object} props.filters - Filters selected by user.
 * @param {Boolean} props.isSearching - If is searching currently.
 * @param {Boolean} props.isPagination - If is using pagination.
 * @param {() => void} props.onClearFilters - Callback to reset all filters.
 * @param {() => void} props.onSearch - Callback to search programs based on filters.
 * @returns {JSX.Element} The rendered FundingFilters component.
 *
 * @example
 * <FundingFilters
 *   items={filterItems}
 *   onClearFilters={handleClear}
 *   filters={filters}
 *   onSearch={handleSearch}
 *   isSearching={isSearching}
 *   isPagination={isPagination}
 * />
 */
export function FundingFilters({
  items,
  onClearFilters,
  hasUploadedFile,
  filters,
  onSearch,
  isSearching,
  isPagination,
}) {
  const hasFilters = mapStateToHasFilters(filters);

  let payload = mapStateDataToApi(filters);
  const hasCategoryFiltersOnly = Object.keys(payload).every(key =>
    [
      ...Object.values(ProgramFilterStatusBackEndKey),
      'gtInsightsPrograms',
    ].includes(key)
  );

  const handleSearch = () => {
    onSearch(payload);
  };

  const handleClearFilters = () => {
    onClearFilters(isPagination);
  };

  const isClearButtonDisabled =
    (!hasFilters && !isPagination) ||
    (isPagination && hasCategoryFiltersOnly) ||
    isSearching ||
    hasUploadedFile;

  return (
    <>
      <div className={`collapse-filters ${styles.filtersContainer}`}>
        <div className="clear-filter-container">
          <Button
            disabled={isClearButtonDisabled}
            onClick={handleClearFilters}
            className={styles.button}
            id={styles['clear-button']}
          >
            {Constants.Clear}
          </Button>
          <Button
            onClick={handleSearch}
            disabled={isSearching || hasUploadedFile}
            className={styles.button}
            id={styles['search-button']}
            htmlType="submit"
          >
            {isSearching ? <Spin size="default" /> : Constants.Search}
          </Button>
        </div>
        <Collapse items={items} />
      </div>
    </>
  );
}
